import FormControl from "common/components/FormControl/FormControl";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isPasswordCorrect } from "common/components/Password/Password";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import { checkEmail } from "common/utils/fieldsValidators";
import CrudForm from "common/components/Crud/CrudForm";
import RadioBlockItem from "common/components/RadioBlockItem/RadioBlockItem";
import { Field } from "react-final-form";
import type { User } from "types";
import OrganizationSelect from "components/select/OrganizationSelect/OrganizationSelect";

type Props = {
  onSubmit: (values: any) => any;
  onCancel?: () => void;
  item?: User;
  myAccount?: boolean;
};

function AdminForm({ onSubmit, onCancel, item, myAccount }: Props) {
  const { t } = useTranslation();

  const [changePassword, setChangePassword] = useState(false);

  const initialValues = useMemo(
    () =>
      item?.id
        ? {
            ...item,
            email: item?.email?.includes("fakemail.com")
              ? ""
              : item?.email || "",
            organization: item.organization?.id,
            role: item?.role?.id,
          }
        : {
            name: undefined,
            password: undefined,
            email: "",
            role: 4,
            notificationSettings: {
              appointment_update: {
                push: true,
                email: true,
              },
              missing_report: {
                push: true,
                email: true,
              },
            },
          },
    [item],
  );

  const checkPassword = (value: string) =>
    isPasswordCorrect(value) ? undefined : t("components.password.tooWeak");

  return (
    <CrudForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      headerIcon={myAccount ? "UserCircleIcon" : "UsersIcon"}
      i18nNamespace={myAccount ? "myAccount" : "users"}
      render={() => (
        <div className="white-box flex flex-col gap-4 mt-2">
          {!myAccount && (
            <Field name="role">
              {({ input }) => (
                <div>
                  <span className="text-sm font-medium text-slate-600">
                    {t("users.role")}
                  </span>
                  <div className="flex gap-2">
                    <RadioBlockItem
                      id={4}
                      icon="UsersIcon"
                      title={t("users.admin")}
                      subtitle={t("users.adminSubtitle")}
                      selected={input.value === 4}
                      onClick={() => input.onChange(4)}
                      className="flex-1"
                      color="secondary"
                    />
                    <RadioBlockItem
                      id={5}
                      icon="UserIcon"
                      title={t("users.manager")}
                      subtitle={t("users.managerSubtitle")}
                      selected={input.value === 5}
                      onClick={() => input.onChange(5)}
                      className="flex-1"
                      color="ternary"
                    />
                  </div>
                </div>
              )}
            </Field>
          )}

          <div className="flex flex-row gap-4">
            <FormControl
              name="username"
              title={t("users.username")}
              required
              containerStyle="flex-1"
            />
            <FormControl
              name="email"
              title={t("users.email")}
              validate={(val: string) => checkEmail(val, t, false)}
              containerStyle="flex-1"
            />
          </div>

          <FormControl
            name="organization"
            title={t("users.frontendOrganization")}
            type="custom"
            isSelect
            required
          >
            <OrganizationSelect />
          </FormControl>

          {item?.id ? (
            <>
              <ToggleSwitch
                rightLabel={t("users.newPassword")}
                value={changePassword}
                onChange={setChangePassword}
              />
              {changePassword && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <FormControl
                    type="jpassword"
                    name="password"
                    title={t("users.password")}
                    helpText={t("users.passwordInstruction")}
                    required
                    validate={checkPassword}
                  />
                  <FormControl
                    name="confirmPassword"
                    title={t("users.confirmPassword")}
                    type="password"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <FormControl
                type="jpassword"
                name="password"
                title={t("users.password")}
                helpText={t("users.passwordInstruction")}
                required
                validate={checkPassword}
              />
              <FormControl
                name="confirmPassword"
                title={t("users.confirmPassword")}
                type="password"
              />
            </div>
          )}
        </div>
      )}
    />
  );
}
export default AdminForm;
